import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, defineMessages, Link } from 'ouisys-component-library';
import { MSISDNEntryFailure, MSISDNEntrySuccess } from 'ouisys-engine/pinFlow';
import { tracker } from 'ouisys-engine/utilities/tracker';
import { handleFormSubmit } from 'ouisys-engine/utilities/handleSubmitNumber';
import { searchToObject } from 'ouisys-engine/utilities/searchToObject';
import PhoneInput from 'ouisys-phone-input/dist/common/PhoneInput';
import { FormattedMessage, injectIntl } from '../../localization/index';
import Loader from '../Loader';
import { useRootContext } from '../../providers/RootContext';
import { RDS, useStrategyState } from 'ouisys-engine/strategy';
import { useSelector } from 'react-redux';
import { getConfig } from 'ouisys-phone-input/dist/common/PhoneInput';
import ErrorModal from '../ErrorModal';

const defaultMessages = defineMessages({
  PhoneInputPlaceholder: {
    id: 'PhoneInputPlaceholder',
    defaultMessage: 'Mobile Number'
  }
});

interface PhoneNumberEntryStepProps {
  rds: RDS.RemoteDataState<MSISDNEntryFailure, MSISDNEntrySuccess>;
  onEnd: (msisdn: string, extraParams?: { [key: string]: string }) => void;
  intl: any;
}

const PhoneNumberEntryStep = (props: PhoneNumberEntryStepProps) => {
  const strategyState = useStrategyState(useSelector);
  const msisdnPrefix = useMemo(() => getConfig(process.env.country).commonPrefix || '', [process.env.country]);
  const { intl } = props;
  const { msisdn, setMsisdn, setShowModal } = useRootContext();
  const { isHeaderEnrichmentSuccess } = strategyState?.data || {};
  const [internationalNumber, setInternationalNumber] = useState<string>(msisdn);
  const [isValid, setIsValid] = useState(false);
  const [isMsisdnFilled, setIsMsisdnFilled] = useState(false);
  const [isKeyEntryStated, setIsKeyEntryStated] = useState<boolean>(false);
  const phoneInputRef = useRef<HTMLInputElement>();
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const urlParams = searchToObject(window);

  useEffect(() => {
    const msisdnFromUrlParams = urlParams['msisdn'];
    setMsisdn(
      msisdnFromUrlParams
        ? msisdnFromUrlParams
        : !msisdn
          ? msisdnPrefix
          : msisdn
    );
  }, [msisdnPrefix]);

  // Peru - showModal if there is an error
  const RDSisFailure: boolean = RDS.IsFailure(props.rds);
  useEffect(() => {
    if (RDSisFailure) {
      setShowModal(RDSisFailure);
    }
  }, [RDSisFailure]);

  return (
    <form
      className="PhoneNumberEntryStep subscriptionArea"
      onSubmit={(ev) => {
        ev.preventDefault();
        handleFormSubmit({
          window,
          isHeaderEnrichmentSuccess,
          country: process.env.country as string,
          setInternationalNumber,
          onEnd: props.onEnd,
          internationalNumber,
          isValid
        });
      }}
    >

      <div className="number-entry">
        {(!isHeaderEnrichmentSuccess && (
          // normal msisdn entry
          <div>
            <div className="msisdnLabel">
              <FormattedMessage id="MsisdnLabel" defaultMessage="Enter your mobile number below to start watching the video now." />
            </div>
            <div className="phoneInputWrap">
              <PhoneInput
                autoFocus
                inputElementRef={phoneInputRef}
                placeholder={intl.formatMessage(defaultMessages.PhoneInputPlaceholder)}
                msisdn={msisdn}
                countryCode={process.env.country}
                showFlag={false}
                showMobileIcon={true}
                showError={true}
                tracker={tracker}
                onChange={({ msisdn, isValid, internationalNumber }) => {
                  setMsisdn(msisdn);
                  setIsValid(isValid);
                  setInternationalNumber(internationalNumber);
                  if (isValid) {
                    tracker.customEvent('MSISDN-State', 'valid', 'MsisdnValid', { msisdn });
                  }
                }}
                onKeyPress={(ev) => {
                  if (!isMsisdnFilled && ev.target.value.length >= 5) {
                    setIsMsisdnFilled(true);
                  }
                  if (!isKeyEntryStated) {
                    tracker.customEvent('MSISDN-State', 'type', 'MsisdnType', { msisdn });
                    setIsKeyEntryStated(true);
                  }
                }}
                onPaste={(ev) => {
                  if (!isMsisdnFilled) {
                    setIsMsisdnFilled(true);
                  }
                  tracker.customEvent('MSISDN-State', 'copypaste', 'MsisdnPasted');
                }}
                onBlur={(ev) => {
                  if (!isMsisdnFilled && ev.target.value.length >= 5) {
                    setIsMsisdnFilled(true);
                    tracker.customEvent('MSISDN-State', 'autofill', 'AutoFill', { msisdn: ev.target.value });
                  }
                }}
              />
            </div>
            <Button
              id="msisdn-submit-button"
              type="submit"
              className="btn"
              disabled={RDS.IsLoading(props.rds) || !isValid}
            >
              <FormattedMessage id="MsisdnButton" defaultMessage="Continue" />
            </Button>
          </div>
        )) || (
          // header enrichment
          <div>
            <div className="msisdnLabel">
              <FormattedMessage
                id="OneClickSubscribeLabel"
                defaultMessage="Click the button below to subscribe"
              />
            </div>
            
            {
              isConfirmation
                && (
                  <Button id="msisdn-submit-button" className="btn" type="submit" isDcbp>
                    <FormattedMessage
                      id="OneClickContinueButton"
                      defaultMessage="Continue"
                    />
                  </Button>
                )
                || (
                  <Button id="msisdn-subscribe-button" className="btn" onClick={() => setIsConfirmation(true)}>
                    <FormattedMessage
                      id="OneClickSubscribeButton"
                      defaultMessage="Subscribe"
                    />
                  </Button>
                )
            }
          </div>
        )}
        {RDS.WhenLoading(null, () => (
          <div className="loading-wrapper">
            <Loader />
            <div>
              <FormattedMessage id="PleaseWait" defaultMessage="Please wait..." />
            </div>
          </div>
        ))(props.rds)}
      </div>

      {RDS.WhenFailure(null, (err: MSISDNEntryFailure) => (
        <ErrorModal error={err} />
      ))(props.rds)}

    </form>
  );
};

export default injectIntl(PhoneNumberEntryStep);
